import React, { useMemo, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import GoogleSignInButton, { IGoogleLoginError, IGoogleLoginSuccess } from '../button/GoogleSignInButton';
import CustomTooltip from '../tooltip/Tooltip';
import { Button, CircularProgress, Divider, FormControl, Icon, IconButton, Paper, Typography, TextField } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ProjectDialog from '../dialog/ProjectDialog';
import PillButton from '../button/PillButton';

export interface ILoginProps {
    env : string;
    version : string; 
    isLoading : boolean;
    isLoggingIn : boolean;
    manualLogIn : (emailOrUsername : string, password : string) => Promise<void>;
    requestForgottenPassword ?: (emailOrUsername : string) => void | Promise<void>;
    onGoogleSignInSuccess : (response : IGoogleLoginSuccess) => Promise<void>;
    onGoogleSignInFailure : (response : IGoogleLoginError) => void;
    enableSignUp ?: boolean;
    manualSignUp ?: (emailOrUsername : string, password : string) => Promise<void>;
    enableBackgroundShade ?: boolean;
}

/**
 * @param {string} env - The current environment where the application is running (e.g., 'development', 'production'). Used to conditionally render or alter behavior based on the environment. Required.
 * @param {string} version - The current version of the application, possibly used to display version information or for logging purposes. Required.
 * @param {boolean} isLoading - Indicates whether the application is in a loading state, which might disable login actions or show a loader. Required.
 * @param {boolean} isLoggingIn - Indicates whether a login process is currently underway, which might disable the login button or change its state to show progress. Required.
 * @param {(emailOrUsername: string, password: string) => Promise<void>} manualLogIn - Function to manually log in a user using username or email and password. This function returns a promise that resolves when the login process completes. Required.
 * @param {(emailOrUsername: string) => void | Promise<void>} requestForgottenPassword - Function to handle forgotten password requests. This might trigger sending an email or redirecting to a password reset page. Can return void or a promise if asynchronous handling is involved. Required.
 * @param {(response: IGoogleLoginSuccess) => Promise<void>} onGoogleSignInSuccess - Callback function that handles successful sign-ins using Google authentication. The function receives a success response object and returns a promise. Required.
 * @param {(response: IGoogleLoginError) => void} onGoogleSignInFailure - Callback function that handles failures during sign-ins using Google authentication. The function receives an error response object. Required.
 * @param {boolean} [enableSignUp] - Optional flag to enable sign-up functionality directly from the login component, allowing users to navigate to a sign-up form or trigger a sign-up process.
 * @param {(emailOrUsername: string, password: string) => Promise<void>} [manualSignUp] - Optional function to manually sign up a user using an email or username and password. This function returns a promise that resolves when the sign-up process is completed.
 * @param {boolean} [enableBackgroundShade] - Optional flag to apply shading over background image.
 * 
 * @return {React.ReactElement} The React element for the Login component.
 */

const useStyles = makeStyles((_theme : Theme) => createStyles({
    // For the Input Label
    labelRoot: {
        color: 'white',
        '&$labelFocused': { // Use $ for referencing another class within the same style rule
            color: 'white',
        },
    },
    labelFocused: {}, // Empty class, just for referencing with $labelFocused

    // For the OutlinedInput part
    inputRoot: { // Targets MuiOutlinedInput-root
        color: 'white', // Text color
        // For the border (notchedOutline)
        '&$inputFocused $notchedOutline': {
            borderColor: 'white',
        },
        '&:hover $notchedOutline': {
            borderColor: 'white',
        },
    },
    inputFocused: {}, // Empty class for referencing
    notchedOutline: { // Targets MuiOutlinedInput-notchedOutline
        borderColor: 'white',
    },
}));

const Login = (props : ILoginProps) : JSX.Element => {
    const [emailOrUsername, setEmailOrUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [recoveryEmailOrUsername, setRecoveryEmailOrUsername] = useState<string>('');
    const [showForgottenPasswordDialog, setShowForgottenPasswordDialog] = useState<boolean>(false);
    const [showSignUpState, setShowSignUpState] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const styles = useStyles();

    const handleForgottenPasswordDialog = () : void => setShowForgottenPasswordDialog(!showForgottenPasswordDialog);
    const onEmailOrUsernameChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) : void => setEmailOrUsername(e.target.value);
    const onPasswordChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) : void => setPassword(e.target.value);
    const onRecoveryEmailOrUsernameChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) : void => setRecoveryEmailOrUsername(e.target.value);

    const isLoading = useMemo<boolean>(() => {
        return props.isLoading;
    }, [props.isLoading]);

    const isLoggingIn = useMemo<boolean>(() => {
        return props.isLoggingIn;
    }, [props.isLoggingIn]);

    const onClickLogin = async () : Promise<void> => {
        props.manualLogIn(emailOrUsername, password);
        resetFields();
    };

    const onClickSignUp = async () : Promise<void> => {
        if (props.manualSignUp) {
            props.manualSignUp(emailOrUsername, password);
            resetFields();
        }
    };

    const onClickPasswordRecovery = async () : Promise<void> => {
        if (!props.requestForgottenPassword) return;

        props.requestForgottenPassword(recoveryEmailOrUsername);
        setShowForgottenPasswordDialog(false);
    };

    const enterPressed = (event : React.KeyboardEvent<HTMLDivElement>) : void => {
        if (emailOrUsername && password) {
            if (event.key === 'Enter') {
                props.manualLogIn(emailOrUsername, password);
                resetFields();
            }
        }
    };

    const handleOnSignUpClick = () : void => {
        setShowSignUpState(true);
    };

    const handleOnSignInClick = () : void => {
        setShowSignUpState(false);
    };

    const resetFields = () => {
        setEmailOrUsername('');
        setPassword('');
    };

    const toggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const renderPasswordIconButton = useMemo(() => {
        if (showSignUpState && !password) {
            return (
                <CustomTooltip
                    className={'aic'}
                    title={'If you are unsure about your password, it is most likely your ID/Passport number.'}
                >
                    <Icon>info</Icon>
                </CustomTooltip>
            );
        }

        return (
            <CustomTooltip
                className={'aic'}
                title={showPassword ? 'Hide Password' : 'Show Password'}
            >
                <IconButton className={'cw'} onClick={toggleShowPassword}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
            </CustomTooltip>
        );
    }, [showSignUpState, showPassword, password]);

    const renderAccountActionButton = useMemo<React.ReactElement>(() => {
        if (!showSignUpState) {
            return (
                <div className='fdr wfill jcc aic mt10'>
                    <span className='asb fs16 cw'>{ 'Don\'t have an account?' }</span>
                    <div className={'asb'}>
                        <Button
                            className={'jcc fs14 cw'}
                            variant={'text'}
                            onClick={handleOnSignUpClick}
                            disabled={isLoading}>
                            Sign Up Here.
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className='fdr wfill jcc aic mt10'>
                <span className='asb fs16 cw'>Already a user?</span>
                <div className={'asb'}>
                    <Button
                        className={'jcc fs14 cw'}
                        variant={'text'}
                        onClick={handleOnSignInClick}
                        disabled={isLoading}>
                        Login.
                    </Button>
                </div>
            </div>
        );

    }, [showSignUpState]);

    return (
        <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
            <div className={'flx1 fdr posr'}>
                <img src={`${ASSET_BASE}/assets/images/login_background.jpg`} className={'posa hfill wfill ofc'} />
                {props.enableBackgroundShade &&
                    <div className={'bcpd posa wfill hfill op50'}/>
                }
                {
                    isLoggingIn ? (
                        <div className={'flx1 fdc aic jcc'}>
                            <img height={60} src={`${ASSET_BASE}/assets/images/logo.png`} className={'posr post70'} />
                            <CircularProgress size={80} />
                        </div>
                    ) : (
                        <div className={'flx1 fdc aic jcc pt30'}>
                            <Paper className={'fdc w500 ais br10 bcblack login-card-background blur'}>
                                <img
                                    height={100}
                                    width={100}
                                    src={`${ASSET_BASE}/assets/images/logo.png`}
                                    className={'posf posr200 post20n'}/>
                                <div className={'zi2500 p20 fdc aic'}>
                                    <div className={'h40'}/>
                                    {
                                        !showSignUpState 
                                            ? <Typography variant='h6' color='primary' className='flx1 cw fwb'>
                                                LOGIN
                                            </Typography>
                                            : <Typography variant='h6' color='primary' className='flx1 cw fwb'>
                                                SIGN UP
                                            </Typography>
                                    }
                                    <div className={'h20'}/>
                                    <div className={'fdr p5 wfill'}>
                                        <TextField
                                            className={'wfill'}
                                            label={ !showSignUpState ? 'Email Or Employee Number' : 'Employee Number'}
                                            name={'emailOrUsername'}
                                            value={emailOrUsername}
                                            onChange={onEmailOrUsernameChange}
                                            variant='outlined' // Ensure variant is outlined for these classes to apply
                                            InputLabelProps={{
                                                classes: {
                                                    root: styles.labelRoot,
                                                    focused: styles.labelFocused,
                                                },
                                            }}
                                            InputProps={{
                                                classes: {
                                                    root: styles.inputRoot,
                                                    focused: styles.inputFocused,
                                                    notchedOutline: styles.notchedOutline,
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className={'h20'}/>
                                    <div className={`wfill ${showSignUpState ? 'mb20' : 'mb10'}`}>
                                        <div className={'fdr wfill'}>
                                            <TextField
                                                className={'wfill'}
                                                label={'Password'}
                                                name={'password'}
                                                value={password}
                                                type={showPassword ? 'text' : 'password'}
                                                onChange={onPasswordChange}
                                                variant='outlined' // Ensure variant is outlined
                                                InputLabelProps={{
                                                    classes: {
                                                        root: styles.labelRoot,
                                                        focused: styles.labelFocused,
                                                    },
                                                }}
                                                InputProps={{
                                                    classes: {
                                                        root: styles.inputRoot,
                                                        focused: styles.inputFocused,
                                                        notchedOutline: styles.notchedOutline,
                                                    },
                                                    endAdornment: renderPasswordIconButton,
                                                }}
                                                onKeyDown={enterPressed} />
                                        </div>
                                        {/* {
                                            !showSignUpState &&
                                            <div className='fdr jcfe wfill'>
                                                <div className={'jcfe'}>
                                                    <Button
                                                        className={'cw'}
                                                        variant={'text'}
                                                        color='primary'
                                                        size='small'
                                                        onClick={handleForgottenPasswordDialog}
                                                        disabled={isLoading}>
                                                            Forgot Password?
                                                    </Button>
                                                </div>
                                            </div>
                                        } */}
                                    </div>
                                    <div className={'fdr p5 wfill mb10'}>
                                        {
                                            !showSignUpState 
                                                ? <Button
                                                    className={`flx1 br25 bcy cp h48 fs18 ${(!emailOrUsername || !password || isLoading) ? 'bcg3' : ''}`}
                                                    variant='contained'
                                                    onClick={onClickLogin}
                                                    disabled={!emailOrUsername || !password || isLoading}>
                                                    LOGIN
                                                </Button>
                                                : <Button
                                                    className={`flx1 br25 bcy cp h48 fs18 ${(!emailOrUsername || !password || isLoading) ? 'bcg3' : ''}`}
                                                    variant='contained'
                                                    onClick={onClickSignUp}
                                                    disabled={!emailOrUsername || !password || isLoading}>
                                                    SIGN UP
                                                </Button>
                                        }
                                    </div>
                                    {
                                        !showSignUpState &&
                                        <div>
                                            <div className='fdr aic cw'>
                                                <Divider className='bcw w205 zi100' />
                                                <p className='fs15 fw500 ml10 mr10'>OR</p>
                                                <Divider className='bcw w205 zi100'/>
                                            </div>
                                            <div className={'fdr flx1 aic jcc'}>
                                                <GoogleSignInButton
                                                    className={'w250 mt10'}
                                                    onGoogleSignInSuccess={props.onGoogleSignInSuccess}
                                                    onGoogleSignInFailure={props.onGoogleSignInFailure}                              
                                                />
                                            </div>
                                        </div>
                                    }
                                    {
                                        !props.enableSignUp 
                                            ? <div className={'h20'}/>
                                            : renderAccountActionButton
                                    }
                                    {
                                        <Typography variant='body2' color='primary' className='flx1 cw mb10'>
                                            {`v ${props.version}`}
                                        </Typography>
                                    }
                                </div>
                                { showForgottenPasswordDialog && 
                                    <ProjectDialog isOpen={showForgottenPasswordDialog} isLoadingCircular={isLoading}>
                                        <div className='pl20 pr20 pt10 pb20 fdc aic jcc'>
                                            <div className={'fdr jcs aifs wfill pb10'}>
                                                <FormControl>
                                                    <TextField
                                                        required
                                                        autoFocus
                                                        margin='dense'
                                                        id='recovery-email-username'
                                                        label='Email Or Employee number'
                                                        fullWidth
                                                        className={'w250'}
                                                        value={recoveryEmailOrUsername}
                                                        onChange={onRecoveryEmailOrUsernameChange}
                                                    />
                                                </FormControl>
                                            </div>
                                            <div className={'fdr aife jcfe wfill '}>
                                                <Button color='primary' onClick={handleForgottenPasswordDialog} disabled={isLoading}>
                                                            CANCEL
                                                </Button>
                                                <PillButton 
                                                    color={'secondary'} 
                                                    className={'ml15 h30 pt3 pl20 pr20 mb3'} 
                                                    disabled={isLoading} 
                                                    type={'submit'} 
                                                    text={'SUBMIT'} 
                                                    onClick={onClickPasswordRecovery}
                                                />
                                            </div>
                                        </div>
                                    </ProjectDialog>
                                }
                            </Paper>
                        </div>
                    )
                }
            </div>
        </GoogleOAuthProvider>
    );
};

export default Login;
